import { showDialog } from '@/utils/dialogs';
/**
 * SchoolPlanModal — unified tabbed modal that consolidates the previous
 * SchoolPlanModalEnhanced (Plan tab), SeatUpdateModalEnhanced (Koltuklar
 * tab), and PlanChangeRequestsModalEnhanced (Talepler tab) into a single
 * surface, matching the "Okul planını yönet" archetype mockup.
 *
 * The caller decides which tab to land on via `initialTab`. All three
 * tabs talk to the same school billing/seat APIs, so a single modal
 * keeps the user inside the same context instead of bouncing through
 * three nearly identical dialogs.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  Switch,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { ADMIN_COLORS, AdminEmptyState, AdminStatusPill } from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { useTranslation } from '../../hooks/useTranslation';

export type SchoolPlanTab = 'plan' | 'seats' | 'requests';

interface SchoolPlanCurrent {
  currency?: string;
  basePriceCents?: number;
  baseSeats?: number;
  addonPackageSize?: number;
  addonPriceCents?: number;
  /** Currently configured seat target (cap). */
  seatTarget?: number;
  /** Active seats in use right now (informational; lower bound). */
  activeSeats?: number;
}

interface SchoolPlanModalProps {
  visible: boolean;
  schoolId: number;
  schoolName?: string;
  current?: SchoolPlanCurrent;
  initialTab?: SchoolPlanTab;
  onClose: () => void;
  onSaved?: () => void;
}

interface ChangeRequest {
  id: number;
  requester_name?: string;
  requester_email?: string;
  requested_seats: number;
  requested_tier: string;
  note?: string;
  created_at?: string;
}

const formatCurrency = (raw: string | number, currency: string): string => {
  const num = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(num)) return '—';
  return `${num.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency.toUpperCase()}`.trim();
};

function initials(name?: string, fallback?: string): string {
  const source = (name || fallback || '?').trim();
  const parts = source.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

interface FieldRowProps {
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  keyboardType?: 'default' | 'number-pad' | 'decimal-pad';
  suffix?: string;
  disabled?: boolean;
}

function FieldRow({
  icon,
  label,
  hint,
  value,
  onChange,
  placeholder,
  keyboardType = 'default',
  suffix,
  disabled,
}: FieldRowProps) {
  const [focused, setFocused] = useState(false);
  return (
    <View style={{ gap: 6 }}>
      <Text className="text-sm font-semibold" style={{ color: ADMIN_COLORS.text.primary }}>
        {label}
      </Text>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          paddingHorizontal: 12,
          backgroundColor: ADMIN_COLORS.surface,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: focused ? ADMIN_COLORS.primary : ADMIN_COLORS.border,
        }}
      >
        <MaterialIcons name={icon} size={18} color={ADMIN_COLORS.text.muted} />
        <TextInput
          value={value}
          onChangeText={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          placeholderTextColor={ADMIN_COLORS.text.muted}
          keyboardType={keyboardType}
          editable={!disabled}
          style={{
            flex: 1,
            paddingVertical: 12,
            fontSize: 14,
            color: ADMIN_COLORS.text.primary,
          }}
        />
        {suffix ? (
          <Text className="text-xs font-semibold" style={{ color: ADMIN_COLORS.text.muted }}>
            {suffix}
          </Text>
        ) : null}
      </View>
      {hint ? (
        <Text className="text-xs" style={{ color: ADMIN_COLORS.text.muted }}>
          {hint}
        </Text>
      ) : null}
    </View>
  );
}

export default function SchoolPlanModal({
  visible,
  schoolId,
  schoolName,
  current,
  initialTab = 'plan',
  onClose,
  onSaved,
}: SchoolPlanModalProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<SchoolPlanTab>(initialTab);

  // ─── Plan tab state ──────────────────────────────────────────────────
  const [currency, setCurrency] = useState(current?.currency || 'try');
  const [basePrice, setBasePrice] = useState(
    current?.basePriceCents ? (current.basePriceCents / 100).toFixed(2) : '20000.00',
  );
  const [baseSeats, setBaseSeats] = useState(String(current?.baseSeats ?? 5));
  const [pkgSize, setPkgSize] = useState(String(current?.addonPackageSize ?? 5));
  const [pkgPrice, setPkgPrice] = useState(
    current?.addonPriceCents ? (current.addonPriceCents / 100).toFixed(2) : '15000.00',
  );

  // ─── Seats tab state ─────────────────────────────────────────────────
  const baseSeatTarget = Math.max(current?.seatTarget ?? current?.baseSeats ?? 5, 1);
  const minSeats = Math.max(current?.activeSeats ?? 1, 1);
  const [seatTarget, setSeatTarget] = useState<number>(baseSeatTarget);
  const [prorate, setProrate] = useState(false);

  useEffect(() => {
    if (visible) {
      setTab(initialTab);
      setSeatTarget(baseSeatTarget);
    }
  }, [visible, initialTab, baseSeatTarget]);

  // ─── Requests tab state ──────────────────────────────────────────────
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [decidingId, setDecidingId] = useState<number | null>(null);

  const loadRequests = React.useCallback(async () => {
    try {
      setRequestsLoading(true);
      setRequestsError(null);
      const data = await apiRequest<ChangeRequest[]>({
        endpoint: `/schools/${schoolId}/plan-change-requests?status=pending`,
      });
      setRequests(data);
    } catch (e: any) {
      setRequestsError(e?.message || t('schoolPlan.errors.loadRequests'));
    } finally {
      setRequestsLoading(false);
    }
  }, [schoolId, t]);

  useEffect(() => {
    if (visible && tab === 'requests') loadRequests();
  }, [visible, tab, loadRequests]);

  // ─── Save / submit ───────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const seatDiff = seatTarget - baseSeatTarget;
  const seatTooLow = seatTarget < minSeats;

  const planChanged =
    currency !== (current?.currency || 'try') ||
    Math.round(parseFloat(basePrice.replace(',', '.')) * 100) !== current?.basePriceCents ||
    parseInt(baseSeats, 10) !== current?.baseSeats ||
    parseInt(pkgSize, 10) !== current?.addonPackageSize ||
    Math.round(parseFloat(pkgPrice.replace(',', '.')) * 100) !== current?.addonPriceCents;

  const planSummary = useMemo(() => {
    const cur = currency.toUpperCase();
    const seatsN = parseInt(baseSeats, 10);
    const pkgN = parseInt(pkgSize, 10);
    return {
      base: formatCurrency(basePrice, cur),
      addon: formatCurrency(pkgPrice, cur),
      seats: Number.isFinite(seatsN) ? seatsN : 0,
      pkg: Number.isFinite(pkgN) ? pkgN : 0,
    };
  }, [currency, basePrice, baseSeats, pkgSize, pkgPrice]);

  const seatPriceProjection = useMemo(() => {
    const baseCents = Math.round(parseFloat(basePrice.replace(',', '.')) * 100) || 0;
    const addonCents = Math.round(parseFloat(pkgPrice.replace(',', '.')) * 100) || 0;
    const baseSeatsN = parseInt(baseSeats, 10) || 0;
    const pkgN = parseInt(pkgSize, 10) || 1;
    if (!baseSeatsN || !pkgN) return { current: 0, next: 0 };
    const baseCount = Math.max(0, baseSeatTarget - baseSeatsN);
    const nextCount = Math.max(0, seatTarget - baseSeatsN);
    const basePackages = Math.ceil(baseCount / pkgN);
    const nextPackages = Math.ceil(nextCount / pkgN);
    return {
      current: baseCents + basePackages * addonCents,
      next: baseCents + nextPackages * addonCents,
    };
  }, [basePrice, pkgPrice, baseSeats, pkgSize, baseSeatTarget, seatTarget]);

  const submitPlan = async () => {
    setSaveError(null);
    const baseCents = Math.round(parseFloat(basePrice.replace(',', '.')) * 100);
    const addonCents = Math.round(parseFloat(pkgPrice.replace(',', '.')) * 100);
    const seats = parseInt(baseSeats, 10);
    const pkg = parseInt(pkgSize, 10);
    if (
      !Number.isFinite(baseCents) ||
      !Number.isFinite(addonCents) ||
      !Number.isFinite(seats) ||
      !Number.isFinite(pkg) ||
      seats < 1 ||
      pkg < 1
    ) {
      setSaveError(
        t('schoolPlan.invalidValues'),
      );
      return;
    }
    try {
      setSaving(true);
      await apiRequest({
        endpoint: `/schools/${schoolId}/billing-plan`,
        method: 'PUT',
        data: {
          billing_currency: currency.trim(),
          billing_base_price_cents: baseCents,
          billing_base_seats: seats,
          billing_addon_package_size: pkg,
          billing_addon_price_cents: addonCents,
        },
      });
      onSaved?.();
      onClose();
    } catch (e: any) {
      const msg = e?.message || t('schoolPlan.savePlanError');
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const submitSeats = async () => {
    setSaveError(null);
    if (seatTooLow) {
      setSaveError(
        t(
          'schoolPlan.seats.tooLowHint',
        ),
      );
      return;
    }
    if (seatDiff === 0) return;
    try {
      setSaving(true);
      await apiRequest({
        endpoint: `/billing/schools/${schoolId}/update-seat-capacity`,
        method: 'POST',
        data: { seat_target: seatTarget, prorate },
      });
      onSaved?.();
      onClose();
    } catch (e: any) {
      const msg = e?.message || t('schoolPlan.saveSeatsError');
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const decideRequest = async (id: number, action: 'approve' | 'deny') => {
    try {
      setDecidingId(id);
      await apiRequest({
        endpoint: `/schools/${schoolId}/plan-change-requests/${id}/${action}`,
        method: 'POST',
      });
      const successMsg =
        action === 'approve'
          ? t('schoolPlan.requests.approveSuccess')
          : t('schoolPlan.requests.rejectSuccess');
      // Still a dialog: this modal stays open and would cover a toast.
      showDialog(successMsg);
      await loadRequests();
      onSaved?.();
    } catch (e: any) {
      const msg =
        e?.message || t('schoolPlan.requests.actionError');
      showDialog(msg);
    } finally {
      setDecidingId(null);
    }
  };

  // ─── Tabs definition ─────────────────────────────────────────────────
  const tabs: ReadonlyArray<{
    key: SchoolPlanTab;
    icon: keyof typeof MaterialIcons.glyphMap;
    label: string;
    badge?: number;
  }> = [
    { key: 'plan', icon: 'description', label: t('schoolPlan.tabs.plan') },
    { key: 'seats', icon: 'people-outline', label: t('schoolPlan.tabs.seats') },
    {
      key: 'requests',
      icon: 'inbox',
      label: t('schoolPlan.tabs.requests'),
      badge: requests.length,
    },
  ];

  const footerLabel: Record<SchoolPlanTab, { saveLabel: string; subtle: string }> = {
    plan: {
      saveLabel: t('schoolPlan.savePlan'),
      subtle: t(
        'schoolPlan.applyHint',
      ),
    },
    seats: {
      saveLabel: t('schoolPlan.saveSeats'),
      subtle: t(
        'schoolPlan.notifyHint',
      ),
    },
    requests: {
      saveLabel: t('schoolPlan.requests.refresh'),
      subtle: t(
        'schoolPlan.requests.subtle',
      ),
    },
  };

  const onPrimary = () => {
    if (tab === 'plan') return submitPlan();
    if (tab === 'seats') return submitSeats();
    return loadRequests();
  };

  const primaryDisabled =
    saving ||
    (tab === 'seats' && (seatDiff === 0 || seatTooLow)) ||
    (tab === 'plan' && !planChanged);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(15, 23, 42, 0.55)',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <View
          style={{
            width: '100%',
            maxWidth: 1040,
            maxHeight: '94%',
            backgroundColor: ADMIN_COLORS.background,
            borderRadius: 22,
            borderWidth: 1,
            borderColor: ADMIN_COLORS.border,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 14,
              padding: 22,
              borderBottomWidth: 1,
              borderBottomColor: ADMIN_COLORS.borderSoft,
            }}
          >
            <View
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                backgroundColor: ADMIN_COLORS.primarySoft,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MaterialIcons name="event" size={24} color={ADMIN_COLORS.primary} />
            </View>
            <View style={{ flex: 1 }}>
              <Text className="text-xl font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
                {t('schoolPlan.title')}
                {schoolName ? ` · ${schoolName}` : ''}
              </Text>
              <Text
                className="text-sm"
                style={{ color: ADMIN_COLORS.text.secondary, marginTop: 2 }}
              >
                {t(
                  'schoolPlan.subtitle',
                )}
              </Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              hitSlop={8}
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: ADMIN_COLORS.surface,
              }}
            >
              <MaterialIcons name="close" size={18} color={ADMIN_COLORS.text.secondary} />
            </TouchableOpacity>
          </View>

          {/* Tab bar */}
          <View style={{ flexDirection: 'row', gap: 8, padding: 16, paddingBottom: 4, flexWrap: 'wrap' }}>
            {tabs.map((tabDef) => {
              const active = tabDef.key === tab;
              return (
                <TouchableOpacity
                  key={tabDef.key}
                  onPress={() => setTab(tabDef.key)}
                  style={{
                    flex: 1,
                    minWidth: 160,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    borderRadius: 12,
                    backgroundColor: active ? ADMIN_COLORS.primary : ADMIN_COLORS.surface,
                    borderWidth: 1,
                    borderColor: active ? ADMIN_COLORS.primary : ADMIN_COLORS.border,
                  }}
                >
                  <MaterialIcons
                    name={tabDef.icon}
                    size={16}
                    color={active ? '#FFFFFF' : ADMIN_COLORS.text.secondary}
                  />
                  <Text
                    style={{
                      fontSize: 13,
                      fontWeight: '600',
                      color: active ? '#FFFFFF' : ADMIN_COLORS.text.secondary,
                    }}
                  >
                    {tabDef.label}
                  </Text>
                  {typeof tabDef.badge === 'number' && tabDef.badge > 0 ? (
                    <View
                      style={{
                        minWidth: 18,
                        height: 18,
                        borderRadius: 9,
                        paddingHorizontal: 6,
                        backgroundColor: active ? '#FFFFFF' : ADMIN_COLORS.warningSoft,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 10,
                          fontWeight: '700',
                          color: active ? ADMIN_COLORS.primary : ADMIN_COLORS.warningText,
                        }}
                      >
                        {tabDef.badge}
                      </Text>
                    </View>
                  ) : null}
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Body */}
          <ScrollView
            contentContainerStyle={{ padding: 22, gap: 18 }}
            showsVerticalScrollIndicator={false}
          >
            {tab === 'plan' ? (
              <View style={{ flexDirection: 'row', gap: 18, flexWrap: 'wrap' }}>
                <View style={{ flex: 1.4, minWidth: 280, gap: 16 }}>
                  <View
                    style={{
                      padding: 18,
                      borderRadius: 16,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.border,
                      backgroundColor: ADMIN_COLORS.background,
                      gap: 14,
                    }}
                  >
                    <Text
                      className="text-sm font-bold"
                      style={{ color: ADMIN_COLORS.text.primary }}
                    >
                      {t('schoolPlan.basePlan')}
                    </Text>
                    <FieldRow
                      icon="public"
                      label={t('schoolPlan.currency')}
                      placeholder="try"
                      value={currency}
                      onChange={setCurrency}
                      hint={t('schoolPlan.currencyHint')}
                      disabled={saving}
                    />
                    <FieldRow
                      icon="payments"
                      label={t('schoolPlan.basePrice')}
                      placeholder="20000.00"
                      value={basePrice}
                      onChange={setBasePrice}
                      keyboardType="decimal-pad"
                      suffix={currency.toUpperCase()}
                      disabled={saving}
                    />
                    <FieldRow
                      icon="event-seat"
                      label={t('schoolPlan.baseSeats')}
                      placeholder="5"
                      value={baseSeats}
                      onChange={setBaseSeats}
                      keyboardType="number-pad"
                      suffix={t('schoolPlan.seatUnit')}
                      disabled={saving}
                    />
                  </View>

                  <View
                    style={{
                      padding: 18,
                      borderRadius: 16,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.border,
                      backgroundColor: ADMIN_COLORS.background,
                      gap: 14,
                    }}
                  >
                    <Text
                      className="text-sm font-bold"
                      style={{ color: ADMIN_COLORS.text.primary }}
                    >
                      {t('schoolPlan.addonPackages')}
                    </Text>
                    <FieldRow
                      icon="inventory-2"
                      label={t('schoolPlan.pkgSize')}
                      placeholder="5"
                      value={pkgSize}
                      onChange={setPkgSize}
                      keyboardType="number-pad"
                      suffix={t('schoolPlan.seatUnit')}
                      disabled={saving}
                    />
                    <FieldRow
                      icon="local-offer"
                      label={t('schoolPlan.pkgPrice')}
                      placeholder="15000.00"
                      value={pkgPrice}
                      onChange={setPkgPrice}
                      keyboardType="decimal-pad"
                      suffix={currency.toUpperCase()}
                      disabled={saving}
                    />
                  </View>
                </View>

                {/* Plan summary sidebar */}
                <View
                  style={{
                    flex: 1,
                    minWidth: 240,
                    padding: 18,
                    borderRadius: 16,
                    backgroundColor: ADMIN_COLORS.primaryFaint,
                    borderWidth: 1,
                    borderColor: ADMIN_COLORS.primaryFaintBorder,
                    gap: 14,
                  }}
                >
                  <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
                    {t('schoolPlan.summary')}
                  </Text>
                  <View>
                    <Text
                      className="text-xs font-semibold uppercase"
                      style={{ color: ADMIN_COLORS.primary, letterSpacing: 0.4 }}
                    >
                      {t('schoolPlan.monthlyBase')}
                    </Text>
                    <Text
                      className="text-2xl font-extrabold"
                      style={{ color: ADMIN_COLORS.text.primary, marginTop: 2 }}
                    >
                      {planSummary.base}
                    </Text>
                    <Text
                      className="text-xs"
                      style={{ color: ADMIN_COLORS.text.secondary, marginTop: 2 }}
                    >
                      {planSummary.seats} {t('schoolPlan.seatUnit')} dahil
                    </Text>
                  </View>
                  <View style={{ height: 1, backgroundColor: ADMIN_COLORS.primaryFaintBorder }} />
                  <View>
                    <Text
                      className="text-xs font-semibold uppercase"
                      style={{ color: ADMIN_COLORS.primary, letterSpacing: 0.4 }}
                    >
                      {t('schoolPlan.perAddon')}
                    </Text>
                    <Text
                      className="text-2xl font-extrabold"
                      style={{ color: ADMIN_COLORS.text.primary, marginTop: 2 }}
                    >
                      {planSummary.addon}
                    </Text>
                    <Text
                      className="text-xs"
                      style={{ color: ADMIN_COLORS.text.secondary, marginTop: 2 }}
                    >
                      +{planSummary.pkg} {t('schoolPlan.seatUnit')}
                    </Text>
                  </View>
                </View>
              </View>
            ) : tab === 'seats' ? (
              <View style={{ flexDirection: 'row', gap: 18, flexWrap: 'wrap' }}>
                <View style={{ flex: 1.4, minWidth: 280, gap: 16 }}>
                  {/* Mevcut durum */}
                  <View
                    style={{
                      padding: 16,
                      borderRadius: 16,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.border,
                      backgroundColor: ADMIN_COLORS.background,
                      gap: 12,
                    }}
                  >
                    <Text
                      className="text-sm font-bold"
                      style={{ color: ADMIN_COLORS.text.primary }}
                    >
                      {t('schoolPlan.seats.currentStatus')}
                    </Text>
                    <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
                      {[
                        {
                          icon: 'workspace-premium' as const,
                          label: t('schoolPlan.seats.currentPlan'),
                          value: t('schoolPlan.seats.currentPackage'),
                        },
                        {
                          icon: 'people-outline' as const,
                          label: t('schoolPlan.seats.activeSeats'),
                          value: `${current?.activeSeats ?? '—'} / ${baseSeatTarget}`,
                        },
                        {
                          icon: 'attach-money' as const,
                          label: t('schoolPlan.seats.currency'),
                          value: currency.toUpperCase(),
                        },
                      ].map((s) => (
                        <View
                          key={s.label}
                          style={{
                            flex: 1,
                            minWidth: 130,
                            padding: 12,
                            borderRadius: 12,
                            backgroundColor: ADMIN_COLORS.surface,
                            borderWidth: 1,
                            borderColor: ADMIN_COLORS.borderSoft,
                            gap: 6,
                          }}
                        >
                          <View
                            style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
                          >
                            <MaterialIcons
                              name={s.icon}
                              size={14}
                              color={ADMIN_COLORS.text.muted}
                            />
                            <Text
                              className="text-xs font-semibold"
                              style={{ color: ADMIN_COLORS.text.muted }}
                            >
                              {s.label}
                            </Text>
                          </View>
                          <Text
                            className="text-sm font-bold"
                            style={{ color: ADMIN_COLORS.text.primary }}
                          >
                            {s.value}
                          </Text>
                        </View>
                      ))}
                    </View>
                  </View>

                  {/* Yeni koltuk hedefi */}
                  <View
                    style={{
                      padding: 16,
                      borderRadius: 16,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.border,
                      backgroundColor: ADMIN_COLORS.background,
                      gap: 10,
                    }}
                  >
                    <Text
                      className="text-sm font-bold"
                      style={{ color: ADMIN_COLORS.text.primary }}
                    >
                      {t('schoolPlan.seats.newTarget')}
                    </Text>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 12,
                        padding: 8,
                        borderRadius: 14,
                        backgroundColor: ADMIN_COLORS.surface,
                        borderWidth: 1,
                        borderColor: seatTooLow ? ADMIN_COLORS.error : ADMIN_COLORS.border,
                      }}
                    >
                      <TouchableOpacity
                        onPress={() => setSeatTarget((s) => Math.max(minSeats, s - 1))}
                        disabled={saving || seatTarget <= minSeats}
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: ADMIN_COLORS.background,
                          borderWidth: 1,
                          borderColor: ADMIN_COLORS.border,
                          opacity: seatTarget <= minSeats || saving ? 0.5 : 1,
                        }}
                      >
                        <MaterialIcons
                          name="remove"
                          size={20}
                          color={ADMIN_COLORS.text.primary}
                        />
                      </TouchableOpacity>
                      <View style={{ flex: 1, alignItems: 'center' }}>
                        <TextInput
                          value={String(seatTarget)}
                          onChangeText={(v) => {
                            const n = parseInt(v.replace(/[^0-9]/g, ''), 10);
                            setSeatTarget(Number.isFinite(n) ? Math.max(1, n) : 1);
                          }}
                          keyboardType="number-pad"
                          editable={!saving}
                          style={{
                            fontSize: 28,
                            fontWeight: '800',
                            color: ADMIN_COLORS.text.primary,
                            textAlign: 'center',
                            minWidth: 80,
                          }}
                        />
                      </View>
                      <TouchableOpacity
                        onPress={() => setSeatTarget((s) => s + 1)}
                        disabled={saving}
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: ADMIN_COLORS.primarySoft,
                          borderWidth: 1,
                          borderColor: ADMIN_COLORS.primaryFaintBorder,
                        }}
                      >
                        <MaterialIcons name="add" size={20} color={ADMIN_COLORS.primary} />
                      </TouchableOpacity>
                    </View>
                    {seatTooLow ? (
                      <Text className="text-xs" style={{ color: ADMIN_COLORS.errorText }}>
                        {t(
                          'schoolPlan.seats.tooLowHint',
                        )}
                      </Text>
                    ) : (
                      <Text className="text-xs" style={{ color: ADMIN_COLORS.text.muted }}>
                        {t('schoolPlan.seats.minHint', { n: minSeats })}
                      </Text>
                    )}
                  </View>

                  {/* Prorate */}
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                      padding: 16,
                      borderRadius: 14,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.border,
                      backgroundColor: ADMIN_COLORS.background,
                    }}
                  >
                    <View
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 10,
                        backgroundColor: ADMIN_COLORS.primarySoft,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <MaterialIcons name="receipt" size={16} color={ADMIN_COLORS.primary} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text
                        className="text-sm font-semibold"
                        style={{ color: ADMIN_COLORS.text.primary }}
                      >
                        {t('schoolPlan.seats.prorate')}
                      </Text>
                      <Text
                        className="text-xs"
                        style={{ color: ADMIN_COLORS.text.secondary, marginTop: 2 }}
                      >
                        {t(
                          'schoolPlan.seats.prorateHint',
                        )}
                      </Text>
                    </View>
                    <Switch
                      value={prorate}
                      onValueChange={setProrate}
                      trackColor={{ false: '#d1d5db', true: ADMIN_COLORS.primary }}
                      thumbColor="#ffffff"
                    />
                  </View>
                </View>

                {/* Değişiklik özeti */}
                <View style={{ flex: 1, minWidth: 240, gap: 14 }}>
                  <View
                    style={{
                      padding: 18,
                      borderRadius: 16,
                      backgroundColor: ADMIN_COLORS.background,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.border,
                      gap: 12,
                    }}
                  >
                    <Text
                      className="text-sm font-bold"
                      style={{ color: ADMIN_COLORS.text.primary }}
                    >
                      {t('schoolPlan.seats.diffSummary')}
                    </Text>
                    {[
                      {
                        label: t('schoolPlan.seats.seatLabel'),
                        from: String(baseSeatTarget),
                        to: String(seatTarget),
                      },
                      {
                        label: t('schoolPlan.seats.monthlyFee'),
                        from: formatCurrency(seatPriceProjection.current / 100, currency),
                        to: formatCurrency(seatPriceProjection.next / 100, currency),
                      },
                    ].map((row) => (
                      <View
                        key={row.label}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingVertical: 6,
                          borderBottomWidth: 1,
                          borderBottomColor: ADMIN_COLORS.borderSoft,
                        }}
                      >
                        <Text
                          className="text-sm"
                          style={{ color: ADMIN_COLORS.text.secondary }}
                        >
                          {row.label}
                        </Text>
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <Text className="text-sm" style={{ color: ADMIN_COLORS.text.muted }}>
                            {row.from}
                          </Text>
                          <MaterialIcons
                            name="arrow-right-alt"
                            size={16}
                            color={ADMIN_COLORS.primary}
                          />
                          <Text
                            className="text-sm font-bold"
                            style={{ color: ADMIN_COLORS.primary }}
                          >
                            {row.to}
                          </Text>
                        </View>
                      </View>
                    ))}
                    {seatDiff !== 0 ? (
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingTop: 6,
                        }}
                      >
                        <Text
                          className="text-sm"
                          style={{ color: ADMIN_COLORS.text.secondary }}
                        >
                          {t('schoolPlan.seats.diffPackage')}
                        </Text>
                        <Text
                          className="text-sm font-bold"
                          style={{
                            color: seatDiff > 0 ? ADMIN_COLORS.successText : ADMIN_COLORS.warningText,
                          }}
                        >
                          {seatDiff > 0 ? '+' : ''}
                          {seatDiff} {t('schoolPlan.seatUnit')}
                        </Text>
                      </View>
                    ) : null}
                  </View>

                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: 12,
                      borderRadius: 12,
                      backgroundColor: ADMIN_COLORS.warningSoft,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.warningBorder,
                    }}
                  >
                    <MaterialIcons
                      name="info-outline"
                      size={16}
                      color={ADMIN_COLORS.warningText}
                    />
                    <Text
                      className="text-xs flex-1"
                      style={{ color: ADMIN_COLORS.warningText }}
                    >
                      {t(
                        'schoolPlan.seats.notifyHint',
                      )}
                    </Text>
                  </View>

                  {requests.length > 0 ? (
                    <View
                      style={{
                        padding: 14,
                        borderRadius: 14,
                        borderWidth: 1,
                        borderColor: ADMIN_COLORS.border,
                        backgroundColor: ADMIN_COLORS.background,
                        gap: 8,
                      }}
                    >
                      <Text
                        className="text-xs font-semibold uppercase"
                        style={{ color: ADMIN_COLORS.text.muted, letterSpacing: 0.4 }}
                      >
                        {t('schoolPlan.seats.pendingRequests')}
                      </Text>
                      {requests.slice(0, 2).map((r) => (
                        <View
                          key={r.id}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <MaterialIcons
                            name="hourglass-empty"
                            size={14}
                            color={ADMIN_COLORS.warningText}
                          />
                          <Text
                            className="text-xs flex-1"
                            style={{ color: ADMIN_COLORS.text.primary }}
                            numberOfLines={1}
                          >
                            {r.requester_name || r.requester_email} · {r.requested_seats}
                          </Text>
                        </View>
                      ))}
                      <TouchableOpacity onPress={() => setTab('requests')}>
                        <Text
                          className="text-xs font-semibold"
                          style={{ color: ADMIN_COLORS.primary }}
                        >
                          {t('schoolPlan.seats.viewAllRequests')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  ) : null}
                </View>
              </View>
            ) : (
              // Requests tab
              <View style={{ gap: 12 }}>
                {requestsLoading ? (
                  <View style={{ paddingVertical: 60, alignItems: 'center', gap: 12 }}>
                    <ActivityIndicator size="large" color={ADMIN_COLORS.primary} />
                    <Text
                      className="text-sm"
                      style={{ color: ADMIN_COLORS.text.secondary }}
                    >
                      {t('schoolPlan.requests.loading')}
                    </Text>
                  </View>
                ) : requestsError ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: 10,
                      padding: 14,
                      borderRadius: 14,
                      backgroundColor: ADMIN_COLORS.errorSoft,
                      borderWidth: 1,
                      borderColor: ADMIN_COLORS.errorBorder,
                    }}
                  >
                    <MaterialIcons
                      name="error-outline"
                      size={18}
                      color={ADMIN_COLORS.errorText}
                    />
                    <View style={{ flex: 1 }}>
                      <Text
                        className="text-sm font-bold"
                        style={{ color: ADMIN_COLORS.errorText }}
                      >
                        {requestsError}
                      </Text>
                      <TouchableOpacity onPress={loadRequests} style={{ marginTop: 6 }}>
                        <Text
                          className="text-xs font-semibold underline"
                          style={{ color: ADMIN_COLORS.errorText }}
                        >
                          {t('common.retry')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : requests.length === 0 ? (
                  <AdminEmptyState
                    icon="inbox"
                    title={t('schoolPlan.requests.empty')}
                    body={t(
                      'schoolPlan.requests.emptyBody',
                    )}
                  />
                ) : (
                  requests.map((req) => (
                    <View
                      key={req.id}
                      style={{
                        padding: 16,
                        borderRadius: 16,
                        backgroundColor: ADMIN_COLORS.background,
                        borderWidth: 1,
                        borderColor: ADMIN_COLORS.border,
                        gap: 12,
                      }}
                    >
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <View
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 18,
                            backgroundColor: ADMIN_COLORS.primarySoft,
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Text
                            className="text-sm font-bold"
                            style={{ color: ADMIN_COLORS.primary }}
                          >
                            {initials(req.requester_name, req.requester_email)}
                          </Text>
                        </View>
                        <View style={{ flex: 1 }}>
                          <Text
                            className="text-sm font-bold"
                            style={{ color: ADMIN_COLORS.text.primary }}
                          >
                            {req.requester_name || req.requester_email}
                          </Text>
                          {req.requester_name && req.requester_email ? (
                            <Text
                              className="text-xs"
                              style={{ color: ADMIN_COLORS.text.muted }}
                            >
                              {req.requester_email}
                            </Text>
                          ) : null}
                        </View>
                        <AdminStatusPill
                          tone="warning"
                          icon="schedule"
                          label={t('schoolPlan.requests.pending')}
                        />
                      </View>
                      <View
                        style={{
                          flexDirection: 'row',
                          gap: 12,
                          flexWrap: 'wrap',
                        }}
                      >
                        <View
                          style={{
                            flex: 1,
                            minWidth: 140,
                            padding: 10,
                            borderRadius: 12,
                            backgroundColor: ADMIN_COLORS.surface,
                            borderWidth: 1,
                            borderColor: ADMIN_COLORS.borderSoft,
                          }}
                        >
                          <Text
                            className="text-xs font-semibold uppercase"
                            style={{ color: ADMIN_COLORS.text.muted, letterSpacing: 0.4 }}
                          >
                            {t('schoolPlan.requests.requestedSeats')}
                          </Text>
                          <Text
                            className="text-lg font-bold"
                            style={{ color: ADMIN_COLORS.text.primary, marginTop: 2 }}
                          >
                            {req.requested_seats}
                          </Text>
                        </View>
                        <View
                          style={{
                            flex: 1,
                            minWidth: 140,
                            padding: 10,
                            borderRadius: 12,
                            backgroundColor: ADMIN_COLORS.surface,
                            borderWidth: 1,
                            borderColor: ADMIN_COLORS.borderSoft,
                          }}
                        >
                          <Text
                            className="text-xs font-semibold uppercase"
                            style={{ color: ADMIN_COLORS.text.muted, letterSpacing: 0.4 }}
                          >
                            {t('schoolPlan.requests.requestedTier')}
                          </Text>
                          <Text
                            className="text-lg font-bold"
                            style={{ color: ADMIN_COLORS.text.primary, marginTop: 2 }}
                          >
                            {t(`plans.${req.requested_tier}`, req.requested_tier)}
                          </Text>
                        </View>
                      </View>
                      {req.note ? (
                        <View
                          style={{
                            padding: 12,
                            borderRadius: 12,
                            backgroundColor: ADMIN_COLORS.primaryFaint,
                            borderWidth: 1,
                            borderColor: ADMIN_COLORS.primaryFaintBorder,
                          }}
                        >
                          <Text
                            className="text-xs font-semibold uppercase"
                            style={{ color: ADMIN_COLORS.primary, letterSpacing: 0.4 }}
                          >
                            {t('schoolPlan.requests.note')}
                          </Text>
                          <Text
                            className="text-sm"
                            style={{
                              color: ADMIN_COLORS.text.primary,
                              marginTop: 4,
                            }}
                          >
                            {req.note}
                          </Text>
                        </View>
                      ) : null}
                      <View style={{ flexDirection: 'row', gap: 10 }}>
                        <TouchableOpacity
                          disabled={decidingId === req.id}
                          onPress={() => decideRequest(req.id, 'deny')}
                          style={{
                            flex: 1,
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            paddingVertical: 10,
                            borderRadius: 12,
                            borderWidth: 1,
                            borderColor: ADMIN_COLORS.errorBorder,
                            backgroundColor: ADMIN_COLORS.background,
                          }}
                        >
                          {decidingId === req.id ? (
                            <ActivityIndicator size="small" color={ADMIN_COLORS.errorText} />
                          ) : (
                            <>
                              <MaterialIcons name="close" size={16} color={ADMIN_COLORS.errorText} />
                              <Text
                                className="text-sm font-semibold"
                                style={{ color: ADMIN_COLORS.errorText }}
                              >
                                {t('schoolPlan.requests.reject')}
                              </Text>
                            </>
                          )}
                        </TouchableOpacity>
                        <TouchableOpacity
                          disabled={decidingId === req.id}
                          onPress={() => decideRequest(req.id, 'approve')}
                          style={{
                            flex: 1,
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            paddingVertical: 10,
                            borderRadius: 12,
                            backgroundColor: decidingId === req.id ? '#86EFAC' : ADMIN_COLORS.success,
                          }}
                        >
                          {decidingId === req.id ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                          ) : (
                            <>
                              <MaterialIcons name="check" size={16} color="#FFFFFF" />
                              <Text className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>
                                {t('schoolPlan.requests.approve')}
                              </Text>
                            </>
                          )}
                        </TouchableOpacity>
                      </View>
                    </View>
                  ))
                )}
              </View>
            )}

            {saveError ? (
              <View
                style={{
                  flexDirection: 'row',
                  gap: 10,
                  padding: 12,
                  borderRadius: 12,
                  backgroundColor: ADMIN_COLORS.errorSoft,
                  borderWidth: 1,
                  borderColor: ADMIN_COLORS.errorBorder,
                }}
              >
                <MaterialIcons
                  name="error-outline"
                  size={18}
                  color={ADMIN_COLORS.errorText}
                />
                <Text
                  className="text-sm flex-1"
                  style={{ color: ADMIN_COLORS.errorText }}
                >
                  {saveError}
                </Text>
              </View>
            ) : null}
          </ScrollView>

          {/* Footer */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              padding: 18,
              borderTopWidth: 1,
              borderTopColor: ADMIN_COLORS.borderSoft,
              backgroundColor: ADMIN_COLORS.surface,
              flexWrap: 'wrap',
            }}
          >
            <View
              style={{
                flex: 1,
                minWidth: 200,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <View
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: ADMIN_COLORS.primaryFaint,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MaterialIcons name="lock" size={16} color={ADMIN_COLORS.primary} />
              </View>
              <Text className="text-xs flex-1" style={{ color: ADMIN_COLORS.text.secondary }}>
                {footerLabel[tab].subtle}
              </Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 10 }}>
              <TouchableOpacity
                onPress={onClose}
                disabled={saving}
                style={{
                  paddingHorizontal: 18,
                  paddingVertical: 11,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: ADMIN_COLORS.border,
                  backgroundColor: ADMIN_COLORS.background,
                  opacity: saving ? 0.5 : 1,
                }}
              >
                <Text
                  className="text-sm font-semibold"
                  style={{ color: ADMIN_COLORS.text.primary }}
                >
                  {t('common.cancel')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={onPrimary}
                disabled={primaryDisabled}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingHorizontal: 20,
                  paddingVertical: 11,
                  borderRadius: 12,
                  backgroundColor: primaryDisabled ? '#93C5FD' : ADMIN_COLORS.primary,
                }}
              >
                {saving ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <MaterialIcons
                    name={tab === 'requests' ? 'refresh' : 'save'}
                    size={16}
                    color="#FFFFFF"
                  />
                )}
                <Text className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>
                  {footerLabel[tab].saveLabel}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}
